import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { produce } from 'immer';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import reduce from 'lodash/reduce';
import capitalize from 'lodash/capitalize';
import truncate from 'lodash/truncate';

import { IconClose } from '@lightspeed/flame/Icon/Close';
import { IconVerified } from '@lightspeed/flame/Icon/Verified';
import Text from '@lightspeed/cirrus-text';

import Table from 'cirrus/Table';

import AuditTrailDetails from './AuditTrailDetails';
import { apiTransformer, STATUS_CODES, logPropTypes } from './utils';

const tableHead = {
  cells: [
    {
      key: 'action',
      content: <Text body>Action</Text>,
      width: '25%',
    },
    {
      key: 'client',
      content: <Text body>Client</Text>,
      width: '20%',
    },
    {
      key: 'user',
      content: <Text body>User</Text>,
      width: '20%',
    },
    {
      key: 'message',
      content: <Text body>Message</Text>,
      width: '15%',
    },
    {
      key: 'date-time',
      content: 'Date & Time',
      className: 'cr-text--body cr-text--bold cr-truncate',
      align: 'right',
      width: '20%',
    },
  ],
};

const getTableBody = ({ logs, bodyRows, handleClick }) =>
  logs.length && !isEmpty(bodyRows)
    ? reduce(
        logs,
        (body, log) => {
          const logDetails = apiTransformer(log);
          const {
            id,
            action,
            status,
            object,
            objectIDs = [],
            customer = {},
            register = {},
            employee = {},
            message,
            eventTimestamp,
          } = logDetails;

          body.push({
            key: `${logDetails.id}-log`,
            'data-test': `audit-trail-listing-row-${logDetails.id}`,
            hover: true,
            expandable: true,
            expanded: bodyRows[id].expanded,
            className: 'cr-cursor-pointer',
            cells: [
              {
                content: (
                  <div className="cr-flex cr-flex-col">
                    <div className="cr-flex cr-flex-row cr-justify-between cr-mb-1">
                      <div className="cr-text--bold cr-truncate">{action}</div>
                      <div style={{ width: '1rem' }}>
                        {status === STATUS_CODES.FALSE ? (
                          <IconClose color="maple-200" />
                        ) : (
                          <IconVerified color="gray-100" />
                        )}
                      </div>
                    </div>
                    <div className="cr-flex cr-flex-row cr-justify-between">
                      <div className="cr-text--dimmed cr-truncate">{capitalize(object)}</div>
                      <div className="cr-text--dimmed cr-truncate">
                        {objectIDs.length === 1 ? objectIDs[0] : `${objectIDs.length} Objects`}
                      </div>
                    </div>
                  </div>
                ),
                className: 'cr-align-top',
              },
              {
                content: (
                  <div className="cr-flex cr-flex-col">
                    <div className="cr-text--bold cr-truncate cr-mb-1">{customer.name}</div>
                    <div className="cr-text--dimmed cr-truncate">{register.name}</div>
                  </div>
                ),
                className: 'cr-align-top',
              },
              {
                content: employee.name,
                className: 'cr-text--bold cr-align-top cr-truncate',
              },
              {
                content: truncate(message, { length: 50, separator: ' ' }),
                className: 'cr-align-top cr-text--dimmed',
              },
              {
                content: eventTimestamp && moment(eventTimestamp).format('h:mma D MMM, YYYY'),
                className: 'cr-align-top cr-truncate',
              },
            ],
            onClick: () => handleClick(id),
          });

          if (bodyRows[id].expanded) {
            body.push({
              key: `${id}-details`,
              'data-test': `audit-trail-listing-row-${logDetails.id}-details`,
              cells: [
                {
                  content: <AuditTrailDetails log={logDetails} />,
                  className: 'cr-bg-snow-200 cr-inner-shadow-2',
                  style: { padding: 0 },
                  colSpan: 5,
                },
              ],
            });
          }
          return body;
        },
        []
      )
    : [];

class AuditTrailListing extends Component {
  constructor(props) {
    super(props);

    this.state = {
      bodyRows: [],
    };

    this.handleClick = this.handleClick.bind(this);
  }

  componentDidMount() {
    if (this.props.isLoading === false && this.props.dataInit === true) {
      this.updateBodyRows();
    }
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps, this.props)) {
      this.updateBodyRows();
    }
  }

  updateBodyRows() {
    this.setState({
      bodyRows: reduce(
        this.props.logs,
        (logs, log) => {
          logs[log.id] = { expanded: false, hover: false }; // eslint-disable-line no-param-reassign
          return logs;
        },
        {}
      ),
    });
  }

  handleClick(id) {
    this.setState(prevState =>
      produce(prevState, draft => {
        draft.bodyRows[id].expanded = !draft.bodyRows[id].expanded; // eslint-disable-line no-param-reassign
      })
    );
  }

  render() {
    const { logs, isLoading, dataInit } = this.props;
    const { bodyRows } = this.state;

    return (
      <Table
        head={tableHead}
        body={getTableBody({
          logs,
          bodyRows,
          handleClick: this.handleClick,
        })}
        isLoading={isLoading}
        dataInit={dataInit}
        noDataMessage="No log entries found."
      />
    );
  }
}

AuditTrailListing.propTypes = {
  logs: PropTypes.arrayOf(logPropTypes),
  isLoading: PropTypes.bool,
  dataInit: PropTypes.bool,
};

export default AuditTrailListing;
